'use client';

// `/forgot-password` клиентская форма (add-credentials-management).
// Источник истины: `contract.md` §F.2 (дословный JSX-каркас), спека §4.2.1/§4.2.3.
//
// Паритет визуальной системы `/login` (`auth-page.module.css`, Container 460px,
// glass-panel, gradient CTA, Badge-eyebrow).
//
// State-машина (спека §4.2.3):
//   • `idle` — форма (пояснение + поле email + кнопка).
//   • `Saving` — кнопка `loading`, поле `disabled`.
//   • `sent` — экран-сообщение «Если этот email зарегистрирован…»
//     (пояснение idle скрыто). Унифицированный ответ (не раскрывает
//     существование аккаунта) → success это основной ответ сценария,
//     не notification (контракт §F.2).
//
// Ссылка «Вспомнили пароль? Войти» → `/login` рендерится в ОБЩЕЙ части карточки
// (вне тернарника idle/sent) → видна всегда, в т.ч. в idle-форме (спека §4.2.1,
// parity с /login «Создать аккаунт» и /register «Войти в систему»).
//
// Submit-flow (спека §4.2.3):
//   1. Клиентская валидация email (непустое + формат) → `errors.email`, submit прерывается.
//   2. `requestPasswordReset({ email })` → успех → `setSent(true)`.
//   3. Catch: `VALIDATION_ERROR`+`fields.email` → inline; `RATE_LIMITED` → Alert;
//      прочее → `setSubmitError('Не удалось отправить инструкции. Попробуйте позже.')`.

import { useState } from 'react';
import type { CSSProperties } from 'react';
import Link from 'next/link';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { requestPasswordReset } from '@/lib/api/auth-client';
import { PortalApiError } from '@/lib/api/errors';
import { useStableColorScheme } from '@/lib/use-stable-color-scheme';
import styles from '../auth-page.module.css';

export default function ForgotPasswordForm() {
  const theme = useMantineTheme();
  const colorScheme = useStableColorScheme();
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sentState, setSentState] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isDark = colorScheme === 'dark';
  const pageGradient = isDark ? theme.other.pageGradientDark : theme.other.pageGradientLight;
  const panelColor = isDark ? theme.other.panelColorDark : theme.other.panelColorLight;
  const lineColor = isDark ? theme.other.lineColorDark : theme.other.lineColorLight;
  const mutedColor = isDark ? '#b7abd7' : '#6e6790';

  const authVars = {
    '--auth-page-gradient': pageGradient,
    '--auth-panel-bg': panelColor,
    '--auth-panel-strong': isDark ? theme.other.panelStrongColorDark : theme.other.panelStrongColorLight,
    '--auth-line-color': lineColor,
    '--auth-muted-color': mutedColor,
    '--auth-accent-start': theme.other.brandAccentStart,
    '--auth-accent-end': theme.other.brandAccentEnd,
    '--auth-brand-shadow': theme.other.brandButtonShadow,
    '--auth-control-radius': theme.other.controlBorderRadius,
  } as CSSProperties;

  const validateForm = () => {
    const newErrors: { email?: string } = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Пожалуйста, введите электронную почту';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Пожалуйста, введите корректный адрес электронной почты';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setSubmitError(null);
    try {
      await requestPasswordReset({ email: formData.email });
      setSentState(true);
    } catch (error: unknown) {
      if (error instanceof PortalApiError) {
        if (error.code === 'VALIDATION_ERROR' && error.fields?.email) {
          setErrors({ email: error.fields.email });
        } else if (error.code === 'RATE_LIMITED') {
          setSubmitError(error.message);
        } else {
          setSubmitError('Не удалось отправить инструкции. Попробуйте позже.');
        }
      } else {
        setSubmitError('Не удалось отправить инструкции. Попробуйте позже.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="main"
      className={styles.pageWrapper}
      style={{ background: pageGradient, ...authVars }}
      suppressHydrationWarning
    >
      <Box className={styles.mainContent} px={{ base: 'md', sm: 28 }} py={{ base: 32, sm: 48 }}>
        <Container size={460} px={0}>
          <Paper
            className={styles.card}
            radius={theme.other.panelBorderRadius}
            px={{ base: 20, sm: 36 }}
            py={{ base: 24, sm: 36 }}
            withBorder
            shadow="lg"
            style={{
              borderWidth: '1px',
              background: panelColor,
              borderColor: lineColor,
              backdropFilter: 'blur(14px)',
            }}
            suppressHydrationWarning
          >
            <Stack gap="lg">
              <Badge variant="light" className={styles.eyebrow} size="lg" radius="xl">
                Восстановление
              </Badge>

              <Title order={1} size="h2" fw={800} ta="center" className={styles.title}>
                Восстановление пароля
              </Title>

              {sentState ? (
                <Text ta="center">
                  Если этот email зарегистрирован, инструкции отправлены на почту.
                </Text>
              ) : (
                <>
                  <Text
                    size="sm"
                    c={mutedColor}
                    ta="center"
                    className={styles.description}
                    mx="auto"
                  >
                    Введите email, указанный при регистрации. Мы отправим инструкции для
                    восстановления.
                  </Text>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="lg">
                      {submitError && (
                        <Alert color="red" radius="md" variant="light">
                          {submitError}
                        </Alert>
                      )}

                      <TextInput
                        label="Email"
                        placeholder="example@company.ru"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ email: e.target.value })}
                        error={errors.email}
                        required
                        disabled={isLoading}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        loading={isLoading}
                        mt={8}
                        variant="gradient"
                        className={styles.submitButton}
                      >
                        {isLoading ? 'Отправка...' : 'Отправить инструкции'}
                      </Button>
                    </Stack>
                  </form>
                </>
              )}

              <Text ta="center" size="14px">
                <Anchor component={Link} href="/login" c="violet" fw={600} td="none">
                  Вспомнили пароль? Войти
                </Anchor>
              </Text>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
